import React, { useState, useEffect } from 'react';
import axios from 'axios';
import theme from '../theme';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAuth } from '../utils/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Couleurs par rôle ──────────────────────────────────────────────────────────
const ROLE = {
  super: { border: '#7c3aed', bg: '#f5f3ff', color: '#7c3aed', label: '👑 Super Admin' },
  admin: { border: '#3498db', bg: '#ebf5ff', color: '#3498db', label: '👔 Admin'       },
  inact: { border: '#95a5a6', bg: '#f5f5f5', color: '#95a5a6', label: '—'             },
};
const roleInfo = (c) => c.actif === false ? ROLE.inact : c.is_super_admin ? ROLE.super : ROLE.admin;

const Collaborateurs = () => {
  const isMobile    = useIsMobile();
  const { user }    = useAuth();
  const isSuperAdmin = user?.is_super_admin === true;
  const token  = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const [collaborateurs, setCollaborateurs] = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [filterRole,     setFilterRole]     = useState('tous');
  const [showModal,      setShowModal]      = useState(false);
  const [editTarget,     setEditTarget]     = useState(null);
  const [showConfirmDel, setShowConfirmDel] = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');

  const emptyForm = {
    nom: '', prenom: '', email: '', telephone: '',
    mot_de_passe: '', confirmPwd: '', role: 'admin', actif: true,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { fetchCollab(); }, []);

  const fetchCollab = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/utilisateurs`, config);
      const all = res.data.data || [];
      setCollaborateurs(all.filter(u => u.role === 'admin'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditTarget(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (collab) => {
    setEditTarget(collab);
    setForm({
      nom:          collab.nom       || '',
      prenom:       collab.prenom    || '',
      email:        collab.email     || '',
      telephone:    collab.telephone || '',
      mot_de_passe: '',
      confirmPwd:   '',
      role:         collab.role      || 'admin',
      actif:        collab.actif !== false,
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.nom.trim() || !form.email.trim()) return setError('Nom et email obligatoires.');
    if (!editTarget && !form.mot_de_passe)       return setError('Mot de passe obligatoire pour un nouveau compte.');
    if (form.mot_de_passe && form.mot_de_passe.length < 6) return setError('Minimum 6 caractères.');
    if (form.mot_de_passe && form.mot_de_passe !== form.confirmPwd) return setError('Les mots de passe ne correspondent pas.');

    setSaving(true);
    try {
      if (editTarget) {
        const payload = { nom: form.nom, prenom: form.prenom, telephone: form.telephone, role: form.role, actif: form.actif };
        if (form.mot_de_passe) payload.mot_de_passe = form.mot_de_passe;
        await axios.put(`${API_URL}/utilisateurs/${editTarget.id_utilisateur}`, payload, config);
      } else {
        await axios.post(`${API_URL}/utilisateurs`, {
          nom: form.nom, prenom: form.prenom, email: form.email,
          telephone: form.telephone, mot_de_passe: form.mot_de_passe,
          role: 'admin', actif: true,
        }, config);
      }
      setShowModal(false);
      fetchCollab();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/utilisateurs/${id}`, config);
      setShowConfirmDel(null);
      fetchCollab();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur suppression');
    }
  };

  // ── Compteurs ─────────────────────────────────────────────────────────────
  const nbSuper  = collaborateurs.filter(c => c.is_super_admin).length;
  const nbAdmin  = collaborateurs.filter(c => !c.is_super_admin).length;

  // ── Filtre ────────────────────────────────────────────────────────────────
  const filtered = collaborateurs.filter(c => {
    const matchSearch = `${c.nom} ${c.prenom} ${c.email}`.toLowerCase().includes(search.toLowerCase());
    const matchRole   = filterRole === 'tous'
      || (filterRole === 'super_admin' ?  c.is_super_admin : !c.is_super_admin);
    return matchSearch && matchRole;
  });

  const FILTERS = [
    { key: 'tous',        label: 'Tous',          count: collaborateurs.length },
    { key: 'admin',       label: '👔 Admins',     count: nbAdmin               },
    { key: 'super_admin', label: '👑 Super Admins',count: nbSuper              },
  ];

  // ── Styles communs ─────────────────────────────────────────────────────────
  const pill = (bg, color) => ({
    fontSize: '11px', fontWeight: '600', padding: '3px 10px',
    borderRadius: '12px', background: bg, color, whiteSpace: 'nowrap',
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: isMobile ? '12px 10px' : '24px', maxWidth: '1100px', margin: '0 auto' }}>

      {/* ── MODAL CRÉATION / MODIFICATION ── */}
      {showModal && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <div style={S.modalHeader}>
              <h2 style={S.modalTitle}>
                {editTarget ? '✏️ Modifier le collaborateur' : '➕ Nouveau collaborateur'}
              </h2>
              <button onClick={() => setShowModal(false)} style={S.closeBtn}>✕</button>
            </div>

            {error && <div style={S.errBox}>⚠️ {error}</div>}

            <form onSubmit={handleSave}>
              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:'12px' }}>
                <Field label="Nom *"  value={form.nom}    onChange={v => setForm(p=>({...p,nom:v}))} />
                <Field label="Prénom" value={form.prenom} onChange={v => setForm(p=>({...p,prenom:v}))} />
              </div>
              {!editTarget && (
                <Field label="Email *" type="email" value={form.email}
                  onChange={v => setForm(p=>({...p,email:v}))} placeholder="email@mkw-solutions.be" />
              )}
              {editTarget && (
                <div style={S.readonlyField}>
                  <span style={S.readonlyLabel}>Email</span>
                  <span style={S.readonlyValue}>{editTarget.email}</span>
                </div>
              )}
              <Field label="Téléphone" value={form.telephone}
                onChange={v => setForm(p=>({...p,telephone:v}))} placeholder="+32 4XX XX XX XX" />

              <div style={{ height:'1px', background:theme.border, margin:'8px 0 20px' }} />

              {isSuperAdmin ? (
                <>
                  <Field
                    label={editTarget ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}
                    type="password" value={form.mot_de_passe}
                    onChange={v => setForm(p=>({...p,mot_de_passe:v}))}
                    placeholder="Minimum 6 caractères" />
                  {(form.mot_de_passe || !editTarget) && (
                    <Field label="Confirmer le mot de passe" type="password" value={form.confirmPwd}
                      onChange={v => setForm(p=>({...p,confirmPwd:v}))} />
                  )}
                </>
              ) : (
                <div style={{ background:'#fefce8', border:'1px solid #fde047', borderRadius:'8px',
                              padding:'10px 14px', marginBottom:'16px', fontSize:'13px', color:'#854d0e' }}>
                  🔒 La modification du mot de passe est réservée au super administrateur.
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <label style={S.lbl}>Statut du compte</label>
                <div style={{ display:'flex', gap:'10px', marginTop:'6px' }}>
                  {[{v:true,l:'✅ Actif'},{v:false,l:'🚫 Inactif'}].map(opt=>(
                    <button key={String(opt.v)} type="button"
                      onClick={() => setForm(p=>({...p,actif:opt.v}))} style={{
                        flex:1, padding:'10px', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px',
                        border: `2px solid ${form.actif===opt.v ? theme.primary : '#e0e0e0'}`,
                        background: form.actif===opt.v ? theme.primaryBg : 'white',
                        color:      form.actif===opt.v ? theme.primary   : '#666',
                      }}>{opt.l}</button>
                  ))}
                </div>
              </div>

              <div style={{ display:'flex', gap:'10px' }}>
                <button type="button" onClick={() => setShowModal(false)}
                  style={{ ...S.btnGrey, flex:'0 0 auto', padding:'13px 20px' }}>Annuler</button>
                <button type="submit" disabled={saving}
                  style={{ ...S.btnPrimary, flex:1, opacity: saving ? 0.7 : 1 }}>
                  {saving ? '⏳ Sauvegarde...' : editTarget ? '💾 Enregistrer' : '✅ Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL CONFIRMATION SUPPRESSION ── */}
      {showConfirmDel && (
        <div style={S.overlay}>
          <div style={{ ...S.modal, maxWidth:'400px', textAlign:'center' }}>
            <div style={{ fontSize:'48px', marginBottom:'16px' }}>⚠️</div>
            <h2 style={{ color:theme.danger, margin:'0 0 12px' }}>Supprimer ce collaborateur ?</h2>
            <p style={{ color:theme.textSecondary, fontSize:'14px', marginBottom:'24px' }}>
              Cette action est irréversible. Toutes les données liées à ce compte seront supprimées.
            </p>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={() => setShowConfirmDel(null)} style={{ ...S.btnGrey, flex:1 }}>Annuler</button>
              <button onClick={() => handleDelete(showConfirmDel)}
                style={{ ...S.btnPrimary, flex:1, background:theme.danger }}>🗑️ Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* ── EN-TÊTE ─────────────────────────────────────────────────────────── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
                    flexWrap:'wrap', gap:'12px', marginBottom:'24px' }}>
        <div>
          <h1 style={{ margin:0, fontSize: isMobile ? '20px' : '24px', color:'#2c3e50', fontWeight:'700' }}>
            👔 Collaborateurs
          </h1>
          <p style={{ margin:'4px 0 0', color:'#666', fontSize:'14px' }}>
            {collaborateurs.length} collaborateur{collaborateurs.length !== 1 ? 's' : ''} —
            <span style={{ color: ROLE.admin.color }}> {nbAdmin} admin{nbAdmin !== 1 ? 's' : ''}</span> ·
            <span style={{ color: ROLE.super.color }}> {nbSuper} super admin{nbSuper !== 1 ? 's' : ''}</span>
          </p>
        </div>
        {isSuperAdmin && (
          <button onClick={openCreate} style={{
            background: theme.gradientPrimary, color:'white', border:'none',
            borderRadius:'8px', padding:'10px 18px', fontSize:'14px', fontWeight:'600', cursor:'pointer',
          }}>
            ➕ Nouveau collaborateur
          </button>
        )}
      </div>

      {/* ── RECHERCHE + FILTRES ──────────────────────────────────────────────── */}
      <div style={{ display:'flex', gap:'12px', marginBottom:'16px', flexWrap:'wrap' }}>
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Rechercher par nom, prénom ou email…"
          style={{ flex:'1 1 220px', padding:'9px 14px', border:`1px solid ${theme.border}`,
                   borderRadius:'8px', fontSize:'14px', outline:'none' }}
        />
        <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilterRole(f.key)} style={{
              padding:'8px 14px', border:'none', borderRadius:'7px', cursor:'pointer',
              fontSize:'13px', fontWeight:'600',
              background: filterRole === f.key ? theme.primary : '#f0f0f0',
              color:      filterRole === f.key ? 'white'       : '#555',
            }}>
              {f.label} <span style={{ opacity:0.75, fontWeight:'400' }}>({f.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── BANDEAU LECTURE SEULE (admin non-super) ─────────────────────────── */}
      {!isSuperAdmin && (
        <div style={{ background:'#fefce8', border:'1px solid #fde047', borderRadius:'8px',
                      padding:'10px 16px', marginBottom:'16px', fontSize:'13px', color:'#854d0e',
                      display:'flex', alignItems:'center', gap:'8px' }}>
          🔒 <span>Consultation uniquement — la gestion des collaborateurs est réservée au super administrateur.</span>
        </div>
      )}

      {/* ── LISTE ────────────────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'40px', color:'#999' }}>⏳ Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px', color:'#999', background:'white', borderRadius:'12px' }}>
          <p style={{ fontSize:'32px', margin:'0 0 8px' }}>👔</p>
          <p style={{ margin:0 }}>{search ? 'Aucun résultat pour cette recherche.' : 'Aucun collaborateur enregistré.'}</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
          {filtered.map(c => {
            const ri    = roleInfo(c);
            const actif = c.actif !== false;
            const dateCreation = c.date_creation
              ? new Date(c.date_creation).toLocaleDateString('fr-BE')
              : null;
            return (
              <div key={c.id_utilisateur} style={{
                background: 'white', borderRadius: '12px', padding: '16px 20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                borderLeft: `4px solid ${ri.border}`,
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: isMobile ? 'flex-start' : 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                gap: isMobile ? '10px' : '12px',
                opacity: actif ? 1 : 0.6,
              }}>
                {/* ── Infos gauche ── */}
                <div style={{ flex: isMobile ? 'none' : '1 1 0', minWidth:0, width:'100%' }}>
                  <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:'6px', marginBottom:'2px' }}>
                    <div style={{ fontWeight:'700', color:'#2c3e50', wordBreak:'break-word',
                                  overflowWrap:'break-word',
                                  fontSize: isMobile ? '14px' : '16px' }}>
                      {c.prenom} {c.nom}
                    </div>
                    {!actif && (
                      <span style={{ fontSize:'11px', color:'#999', background:'#f0f0f0',
                                     padding:'2px 7px', borderRadius:'10px' }}>Désactivé</span>
                    )}
                  </div>  {/* alignItems flex row for name + badge */}
                  <div style={{ fontSize:'13px', color:'#666', marginTop:'2px' }}>{c.email}</div>
                  {c.telephone && (
                    <div style={{ fontSize:'12px', color:'#999', marginTop:'2px' }}>{c.telephone}</div>
                  )}
                  {dateCreation && (
                    <div style={{ fontSize:'11px', color:'#bbb', marginTop:'2px' }}>
                      Membre depuis {dateCreation}
                    </div>
                  )}
                </div>

                {/* ── Badges + actions droite ── */}
                <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap',
                              width: isMobile ? '100%' : 'auto' }}>
                  {/* Badge rôle */}
                  <span style={pill(ri.bg, ri.color)}>{ri.label}</span>

                  {/* Badge statut */}
                  <span style={pill(
                    actif ? '#e8f8f0' : '#fff5f5',
                    actif ? '#27ae60' : '#e53e3e',
                  )}>
                    {actif ? '✅ Actif' : '🚫 Inactif'}
                  </span>

                  {isSuperAdmin && (
                    <>
                      <button onClick={() => openEdit(c)} style={{
                        padding:'7px 14px', background:'#f0f4f8', color:'#4f6d89',
                        border:'none', borderRadius:'7px', cursor:'pointer',
                        fontSize:'13px', fontWeight:'600',
                      }}>
                        ✏️ Modifier
                      </button>
                      <button onClick={() => setShowConfirmDel(c.id_utilisateur)} style={{
                        padding:'7px 12px', background:'#fff5f5', color:'#e53e3e',
                        border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'13px',
                      }}>
                        🗑️
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Composants utilitaires ────────────────────────────────────────────────────

const Field = ({ label, type='text', value, onChange, placeholder }) => (
  <div style={{ marginBottom:'16px' }}>
    <label style={S.lbl}>{label}</label>
    <input type={type} value={value} placeholder={placeholder||''} onChange={e=>onChange(e.target.value)}
      style={S.inp}
      onFocus={e=>(e.target.style.borderColor=theme.primary)}
      onBlur={e=>(e.target.style.borderColor='#e0e0e0')} />
  </div>
);

// ── Styles (modal + boutons uniquement) ───────────────────────────────────────
const S = {
  overlay:      { position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'16px' },
  modal:        { background:'white', borderRadius:'16px', padding:'28px', width:'100%', maxWidth:'520px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' },
  modalHeader:  { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' },
  modalTitle:   { margin:0, fontSize:'18px', fontWeight:'700', color:theme.textPrimary },
  closeBtn:     { background:'none', border:'none', fontSize:'20px', cursor:'pointer', color:'#999', padding:'4px 8px' },
  errBox:       { background:theme.dangerBg, color:theme.danger, border:`1px solid ${theme.dangerBorder||'#fca5a5'}`, borderRadius:'8px', padding:'10px 14px', marginBottom:'16px', fontSize:'13px' },
  readonlyField:{ display:'flex', flexDirection:'column', gap:'4px', marginBottom:'16px' },
  readonlyLabel:{ fontSize:'13px', fontWeight:'600', color:'#444' },
  readonlyValue:{ padding:'11px 13px', background:'#f5f5f5', borderRadius:'8px', fontSize:'14px', color:'#666' },
  lbl:          { display:'block', marginBottom:'5px', fontSize:'13px', fontWeight:'600', color:'#444' },
  inp:          { width:'100%', padding:'11px 13px', border:'1.5px solid #e0e0e0', borderRadius:'8px', fontSize:'14px', boxSizing:'border-box', outline:'none', color:theme.textPrimary, transition:'border-color 0.15s' },
  btnPrimary:   { padding:'12px 18px', background:theme.gradientPrimary, color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'700', fontSize:'13px', boxShadow:'0 3px 10px rgba(79,109,137,0.3)' },
  btnGrey:      { padding:'12px 18px', background:'#95a5a6', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'600', fontSize:'13px' },
};

export default Collaborateurs;
